import { Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { EnvelopeRepositoryService } from '../../services/envelopes/envelope-repository.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Envelope } from '../../interfaces/envelopes/envelope';
import { eEnvelopeStatusType, eEnvelopeType } from '../../enums';
import { Utilities } from '../../utilities';
import { Router } from '@angular/router';
import { interval, Subject, switchMap, takeUntil, takeWhile, tap, timer } from 'rxjs';
import { Order } from '../../interfaces/orders/order';

@Component({
    selector: 'lib-manage-envelope',
    templateUrl: './manage-envelope.component.html',
    styleUrl: './manage-envelope.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManageEnvelopeComponent implements OnDestroy {
  displayedColumns = [
    "logs",
    "added",
    "numOrders",
    "previous",
    "icon",
    "status",
    "next",
    "updateState",
  ]

  protected envelope: Envelope | undefined;
  protected order: Order;
  protected isProcessing = false;
  protected updateStatus: eEnvelopeStatusType | undefined;
  
  private onDestroy$ = new Subject<void>();

  constructor(private envelopesRepository: EnvelopeRepositoryService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router) {
      this.envelope = data.envelope;
      this.order = data.order;
    }
    
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  viewLogs(envelope: Envelope) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['logs'], { queryParams: { tab: 'changes', entityID: envelope.id, entityName: 'Envelope' } })
    );

    window.open(url, '_blank');
  }

  previous(envelope: Envelope) {
    this.envelopesRepository.previous(envelope.id).subscribe();
    this.startPolling(envelope);
  }

  next(envelope: Envelope) {
    this.envelopesRepository.next(envelope.id).subscribe();
    this.startPolling(envelope);
  }

  private startPolling(envelope: Envelope) {
    this.isProcessing = true;
    const currentStatus = envelope.envelopeStatus.id;

    let refresh$ = interval(2500)
      .pipe(
        takeUntil(this.onDestroy$),
        takeUntil(timer(90000)),
        switchMap(() => this.refreshEnvelope(this.order.id)),
        takeWhile(envelopes => envelopes.find(env => env.id === envelope.id)?.envelopeStatus.id !== currentStatus))
      .subscribe({ complete: () => this.isProcessing = false });
  }

  saveStatus(envelope: Envelope) {
    this.isProcessing = true;

    if (!this.updateStatus) {
      return;
    }

    this.envelopesRepository.patchEnvelope(envelope.id, [
      {
        op: "replace",
        value: this.updateStatus,
        path: "envelopeStatus"
      }
    ]).pipe(
      takeUntil(this.onDestroy$),
      switchMap(() => this.refreshEnvelope(this.order.id))
    ).subscribe({ complete: () => this.isProcessing = false });
  }
  
  refreshEnvelope(orderID: string) {
    return this.envelopesRepository.getOrderEnvelopes(orderID).pipe(
      tap(envelopes => this.envelope = envelopes.find(e => e.envelopeType === eEnvelopeType.hubReport)));
  }

  getIcon(envelope: Envelope) {
    return Utilities.getEnvelopeIcon(envelope.envelopeStatus.id);
  }
}
